import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useVariableLoadedListener } from 'hooks/useVariableLoadedListener'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import React, { ReactNode, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button/Button'
import Dropdown from 'UIKit/Dropdown/Dropdown'
import { DropdownProps } from 'UIKit/Dropdown/types'
import Text from 'UIKit/Text/Text'

import { typography } from 'styled-system'
import { Icons } from 'svgs'
import theme from 'theme'
import { ModalColorTheme } from 'theme/modal/colors'
import AlchemyModal, { ModalBody, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'

enum ScaleVariant {
  Mobile,
  Medium,
  Large,
  ExtraLarge,
}

export type ScaleVariantBonus = {
  tagButtonEnable: boolean
}

export const useGameScreenBonusScale = () => {
  const scaleVariantConfig = {
    [ScaleVariant.Mobile]: {
      containerBonusWidth: '100%',
      containerLevelUpWidth: '100%',
      containerHusd: '100%',
      containerCashback: '100%',
      containerAdventureWidth: '100%',
      spacingRight: '0',
      afterRight: '0',
      tagButtonEnable: true,
    },

    [ScaleVariant.Medium]: {
      containerBonusWidth: '24%',
      containerLevelUpWidth: '20%',
      containerAdventureWidth: '24%',
      containerHusd: '16%',
      containerCashback: '16%',

      spacingRight: '8px',
      afterRight: '-4px',
      tagButtonEnable: false,
    },
    [ScaleVariant.Large]: {
      containerBonusWidth: '25%',
      containerLevelUpWidth: '20%',
      containerAdventureWidth: '25%',
      containerHusd: '16%',
      containerCashback: '16%',

      spacingRight: '16px',
      afterRight: '-8px',
      tagButtonEnable: false,
    },
    [ScaleVariant.ExtraLarge]: {
      containerBonusWidth: '25%',
      containerLevelUpWidth: '18%',
      containerAdventureWidth: '25%',

      containerHusd: '16%',
      containerCashback: '16%',
      spacingRight: '16px',
      afterRight: '-8px',
      tagButtonEnable: true,
    },
  }

  const elementRef = useRef(null)
  const containerWidth = useVariableLoadedListener(() => elementRef.current?.clientWidth)
  const { isDesktop } = useMatchBreakpoints()
  const variant = useMemo(() => {
    if (!isDesktop) {
      return ScaleVariant.Mobile
    }
    if (containerWidth <= 750) {
      return ScaleVariant.Medium
    }
    if (containerWidth <= 1080) {
      return ScaleVariant.Large
    }
    return ScaleVariant.ExtraLarge
  }, [containerWidth, isDesktop])

  const scaleVariant = scaleVariantConfig[variant]

  return {
    elementRef,
    scaleVariant,
  }
}

export const StyledTitle = styled(Text)`
  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.colors.textAlt1};
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.24px;
`

export const WrapperContentDropdown = styled(Column)`
  border-radius: 4px;
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 100%;

  margin-top: 4px;
  background: #22262e;
  padding: 12px;
  cursor: auto;
`

const StyledWrapper: React.FC<
  React.PropsWithChildren<Omit<DropdownProps, 'title'> & { title: string | ReactNode }>
> = ({ children, title, dropdownContent, dropdownPosition, style, id, maxWidth, ...props }) => {
  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      maxWidth={maxWidth}
      display="flex"
      id={id}
      style={{
        flexDirection: 'column',
        cursor: 'pointer',
        ...style,
      }}
      className="wrapper"
    >
      <Dropdown
        display="flex"
        style={{
          flexDirection: 'column',
        }}
        dropdownContent={dropdownContent}
        dropdownPosition={dropdownPosition}
        py="4px"
        px={['0', '', '8px', '', '12px']}
        {...props}
      >
        <StyledTitle minWidth="max-content" width="100%" fontSize={['12px !important', '', '10px !important']}>
          {title}
        </StyledTitle>
        <Column mt="4px" flex={1}>
          {children}
        </Column>
      </Dropdown>
    </Box>
  )
}

export const Wrapper = styled(StyledWrapper)`
  border-radius: 4px;
  transition: 0.1s ease-in-out;
  height: auto;

  ${({ theme }) => theme.mediaQueries.sm} {
    height: 48px;

    &:hover {
      background: rgba(255, 255, 255, 0.04);
    }
  }
`

const StyledWrapperProgressContainer = styled(Flex)`
  position: relative;
  width: 100%;

  background: rgba(255, 255, 255, 0.04);
  border-radius: 2px;
  overflow: hidden;
  min-height: 28px;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-height: 24px;
    border-radius: 4px;
  }
`

const StyledProgressBar = styled(Box)<{ $progress: number }>`
  position: absolute;
  top: 0;
  height: 100%;
  max-width: 100%;

  z-index: 2;
  transition: 0.3s;
  background: linear-gradient(252deg, #7f00ff -3.62%, rgba(225, 0, 255, 0.8) 124.68%);
  width: ${({ $progress }) => $progress}%;
`

const StyldProgressContent = styled(Box)`
  position: absolute;
  width: 100%;
  text-align: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  padding: 4px 12px 4px 2px;
`
export const ProgressBar: React.FC<React.PropsWithChildren<{ progress: number } & BoxProps>> = ({
  progress,
  children,
  ...props
}) => {
  return (
    <StyledWrapperProgressContainer {...props}>
      <StyldProgressContent>{children}</StyldProgressContent>
      <StyledProgressBar $progress={progress} />
    </StyledWrapperProgressContainer>
  )
}

export const StyledButton = styled(Button)<{ fontSize?: any; primary?: boolean }>`
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  padding: 8px 12px;
  font-size: ${({ fontSize }) => fontSize || '10px'};
  height: ${({ height }) => height || '40px'};
  font-weight: 600;

  white-space: nowrap;
  text-align: center;

  &:hover {
    transform: scale(1) !important;
  }

  ${({ primary, theme }) =>
    primary &&
    css`
      background: ${theme.colors.primaryAlt};
      border-color: ${theme.colors.primaryAlt};
    `}

  &:disabled {
    background: rgba(255, 255, 255, 0.08);

    ${({ primary, theme }) =>
      primary &&
      css`
        background: ${theme.colors.primaryDisabled};
      `}
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    height: max-content;
  }

  ${typography}
`

export const StyledArrowButton: React.FC<React.PropsWithChildren<BoxProps & { disabled?: boolean }>> = ({
  disabled,
  children,
  ...props
}) => {
  return (
    <RowMiddle
      background="transparent !important"
      p="0 !important"
      display={['flex !important', '', 'none !important']}
      {...props}
    >
      <Text color={disabled ? 'textAlt1' : 'text'} fontSize="12px" mr="4px" bold>
        {children}
      </Text>
      <Icons.ArrowRight width="10px" fill={disabled ? theme.colors.textAlt1 : theme.colors.text} />
    </RowMiddle>
  )
}

export const StyledTag: React.FC<React.PropsWithChildren<BoxProps>> = ({ children, style, ...props }) => {
  return (
    <Text
      color={theme.colors.success}
      px="12px"
      py="6px"
      fontSize="10px"
      fontWeight={700}
      style={{
        background: 'rgba(6, 194, 112, 0.06)',
        borderRadius: theme.radii.large,

        ...style,
      }}
      {...props}
    >
      {children}
    </Text>
  )
}

export const ModalWrapper: React.FC<
  React.PropsWithChildren<
    ModalProps<any> & {
      title: string
    }
  > &
    BoxProps
> = ({ title, children, onDismiss, ...props }) => {
  return (
    <StyledModal colorTheme={ModalColorTheme.Darkblue} onDismiss={onDismiss} className="modal-wrapper" {...props}>
      <ModalHeader colorTheme={ModalColorTheme.Darkblue} title={title} />
      <ModalBody>{children}</ModalBody>
    </StyledModal>
  )
}

const StyledModal = styled(AlchemyModal)`
  .wrap-header-container {
    background: transparent;
  }
`

export const StyledCashbackCountdownContent = styled(Flex)`
  color: ${({ theme: { colors } }) => colors.text};
  margin: 4px auto;

  .day,
  .hour,
  .min,
  .sec {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .value {
      width: 27px;
      height: fit-content;
      display: flex;
      justify-content: center;
      margin-bottom: 6px;
      font-size: 14px;
      font-family: 'Montserrat';
      font-weight: 600;
      color: ${({ theme: { colors } }) => colors.textTertiary};
    }

    .unit {
      font-size: 12px;
      font-weight: 400;
      color: ${({ theme: { colors } }) => colors.textAlt1};
    }
  }

  .colon-mark {
    color: ${({ theme: { colors } }) => colors.textAlt1};
    font-size: 12px;
    font-weight: bold;
    margin-inline: 20px;
    transform: translateX(-50%);
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    margin: 0;

    .unit {
      font-size: 12px;
    }
    .unit {
      font-size: 10px;
    }
    .colon-mark {
      margin-inline: 4px;
    }
  }
`
